/**
 * Returning visitor popup
 * ------------------------------------------------------------------------------------------------
 * Shows a newsletter signup (native customer form) inside a native <dialog>, but only on the visitor's second
 * independent visit (new session after SESSION_TIMEOUT of inactivity) within MAX_VISIT_AGE.
 *
 * - Nothing is read from or written to localStorage before Shopify's Customer Privacy API reports
 *   marketing consent. Without consent (or on revocation) the stored history is deleted.
 * - Concurrent tabs are serialized through the Web Locks API (if available) and kept in sync
 *   through `storage` events, so a new session is only counted once.
 * - Any failure (no consent API, no localStorage) leaves the dialog closed.
 * - After submitting, Shopify reloads the page with #<form id>; the dialog then reopens to show the result
 *   (independent of consent, as it answers the visitor's own action).
 *
 * Markup and settings: sections/returning-visitor-popup.liquid
 */

const STORAGE_KEY = 'returning_visitor_v1';
const STORAGE_VERSION = 1;
const MINUTE = 60 * 1000;
const DAY = 24 * 60 * MINUTE;
const ACTIVITY_THROTTLE = MINUTE;
const CONSENT_API_TIMEOUT = 10000;
const LOCK_CLASS = 'rv-scroll-lock';

/* ---------------------------------------------------------------------------------------------- */
/* Storage                                                                                        */
/* ---------------------------------------------------------------------------------------------- */

const isTimestamp = (value) => Number.isFinite(value) && value > 0;

function isValidState(state, now) {
  return (
    state !== null &&
    typeof state === 'object' &&
    state.version === STORAGE_VERSION &&
    Number.isInteger(state.visits) &&
    state.visits >= 1 &&
    isTimestamp(state.firstVisit) &&
    isTimestamp(state.lastVisit) &&
    state.firstVisit <= state.lastVisit &&
    state.lastVisit <= now + DAY && // timestamps from the future: broken clock or tampered data
    typeof state.lastSessionId === 'string' &&
    typeof state.popupShown === 'boolean' &&
    typeof state.newsletterSubscribed === 'boolean' &&
    typeof state.hasOrdered === 'boolean' &&
    (state.popupSignup === undefined || typeof state.popupSignup === 'boolean') && // added later, optional
    (state.dismissedAt === null || isTimestamp(state.dismissedAt))
  );
}

function storageAvailable() {
  try {
    const probe = '__returning_visitor_probe__';
    localStorage.setItem(probe, probe);
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readState(now = Date.now()) {
  let raw = null;

  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  try {
    const state = JSON.parse(raw);

    if (isValidState(state, now)) {
      return state;
    }
  } catch {
    // fall through: corrupt JSON
  }

  removeState(); // corrupt or outdated data is reset
  return null;
}

function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded / private mode: ignore, the shop keeps working
  }
}

function removeState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function createSessionId() {
  return window.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function createState(now, carryOver = {}) {
  return {
    version: STORAGE_VERSION,
    visits: 1,
    firstVisit: now,
    lastVisit: now,
    lastSessionId: createSessionId(),
    popupShown: carryOver.popupShown ?? false,
    newsletterSubscribed: carryOver.newsletterSubscribed ?? false,
    hasOrdered: carryOver.hasOrdered ?? false,
    popupSignup: carryOver.popupSignup ?? false,
    dismissedAt: carryOver.dismissedAt ?? null
  };
}

/** Runs `callback` exclusively across all tabs of this origin (falls back to running it directly). */
function withLock(callback) {
  if (navigator.locks?.request) {
    return navigator.locks.request(STORAGE_KEY, () => callback());
  }

  return Promise.resolve().then(callback);
}

/** Read-modify-write of the stored state. `mutate` returns false to skip writing. */
function updateState(mutate) {
  return withLock(() => {
    const now = Date.now();
    const state = readState(now);

    if (!state || mutate(state, now) === false) {
      return state;
    }

    writeState(state);
    return state;
  });
}

/**
 * Registers activity. Counts a new visit only if the last activity is older than the session
 * timeout. Resets the history after `maxVisitAge` of inactivity (subscription, order and dismissal
 * flags are kept).
 */
function registerActivity(config) {
  return withLock(() => {
    const now = Date.now();
    let state = readState(now);
    let newSession = false;

    if (!state) {
      state = createState(now);
      newSession = true;
    } else if (now - state.lastVisit > config.maxVisitAge) {
      state = createState(now, state);
      newSession = true;
    } else if (now - state.lastVisit > config.sessionTimeout) {
      state.visits += 1;
      state.lastSessionId = createSessionId();
      newSession = true;
    }

    state.lastVisit = now;
    writeState(state);

    return { state, newSession };
  });
}

/* ---------------------------------------------------------------------------------------------- */
/* Qualification                                                                                  */
/* ---------------------------------------------------------------------------------------------- */

function qualify(state, config, now = Date.now()) {
  if (!state) return 'no state';
  if (config.onlyNonSubscribers && state.newsletterSubscribed) return 'already subscribed';
  if (config.onlyNewCustomers && state.hasOrdered) return 'already ordered';
  if (state.visits < config.requiredVisits) return `visit ${state.visits} of ${config.requiredVisits}`;
  if (now - state.firstVisit > config.maxVisitAge) return 'first visit too long ago';
  if (state.popupShown && state.dismissedAt !== null && now - state.dismissedAt < config.dismissCooldown) return 'cooldown active';

  return null; // qualified
}

/* ---------------------------------------------------------------------------------------------- */
/* Consent (Shopify Customer Privacy API)                                                         */
/* ---------------------------------------------------------------------------------------------- */

function loadCustomerPrivacy() {
  return new Promise((resolve) => {
    const resolveApi = () => resolve(window.Shopify?.customerPrivacy ?? null);

    if (window.Shopify?.customerPrivacy) {
      return resolveApi();
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      return resolve(null);
    }

    const timer = setTimeout(() => resolve(null), CONSENT_API_TIMEOUT);

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      clearTimeout(timer);
      error ? resolve(null) : resolveApi();
    });
  });
}

function marketingAllowed(api) {
  try {
    return api.marketingAllowed() === true;
  } catch {
    return false;
  }
}

/* ---------------------------------------------------------------------------------------------- */
/* Custom element                                                                                 */
/* ---------------------------------------------------------------------------------------------- */

class ReturningVisitorPopup extends HTMLElement {
  #abortController = null;
  #trackingController = null;
  #consent = null;
  #state = null;
  #lastActivity = 0;
  #evaluating = false;
  #initialized = false;
  #config = null;
  #subscribedViaForm = false;
  #popupSignup = false;
  #suppressAutoOpen = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#config = this.#readConfig();
    this.dialog = this.querySelector('dialog');

    if (this.dialog) {
      this.#bindDialog();
    }

    this.#handleFormResult();

    if (window.Shopify?.designMode) {
      this.#bindThemeEditor();
      return;
    }

    this.#init().catch((error) => this.#log('init failed', error));
  }

  disconnectedCallback() {
    this.#stopTracking();
    this.#abortController?.abort();
    this.#unlockScroll();
  }

  #readConfig() {
    const number = (name, fallback) => {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    };

    return {
      sessionTimeout: number('sessionTimeout', 30) * MINUTE,
      requiredVisits: number('requiredVisits', 2),
      maxVisitAge: number('maxVisitAge', 30) * DAY,
      dismissCooldown: number('dismissCooldown', 30) * DAY,
      delay: number('delay', 0) * 1000,
      onlyNewCustomers: this.hasAttribute('data-only-new-customers'),
      onlyNonSubscribers: this.hasAttribute('data-only-non-subscribers'),
      customerHasOrdered: this.hasAttribute('data-customer-has-ordered'),
      customerSubscribed: this.hasAttribute('data-customer-subscribed'),
      debug: this.hasAttribute('data-debug'),
      formId: this.dataset.formId || ''
    };
  }

  #log(...args) {
    if (this.#config?.debug) {
      console.info('[returning-visitor]', ...args);
    }
  }

  /**
   * After a signup Shopify reloads the page with ?customer_posted=true (success) and #<form id>. Our form
   * reopens to show the result; the URL is cleaned up so a reload doesn't show it again.
   */
  #handleFormResult() {
    const url = new URL(window.location.href);
    const formState = this.querySelector('[data-rv-form-state]')?.dataset.rvFormState;
    const ownForm = this.#config.formId !== '' && url.hash === `#${this.#config.formId}`;

    // Any theme newsletter form (footer, car selector, this popup) counts as subscription
    this.#subscribedViaForm = url.searchParams.get('customer_posted') === 'true';

    if (formState && !ownForm) {
      // Another customer form was submitted; its result is rendered into our form too, so don't auto-open
      this.#suppressAutoOpen = true;
      return;
    }

    if (!formState || !ownForm || !this.dialog || window.Shopify?.designMode) {
      return;
    }

    // Read by the double opt-in confirmation page (snippets/email-marketing-confirmation.liquid) to show the coupon hint
    this.#popupSignup = formState === 'success';

    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url.toString());

    this.#open();
  }

  /* Consent ------------------------------------------------------------------------------------ */

  async #init() {
    const api = await loadCustomerPrivacy();

    if (!api) {
      this.#log('Customer Privacy API not available, popup disabled');
      return;
    }

    document.addEventListener('visitorConsentCollected', (event) => {
      this.#onConsentChange(event.detail?.marketingAllowed === true);
    }, { signal: this.#abortController.signal });

    this.#onConsentChange(marketingAllowed(api));
  }

  #onConsentChange(allowed) {
    if (allowed === this.#consent) {
      return;
    }

    const wasAllowed = this.#consent === true;
    this.#consent = allowed;
    this.#log('marketing consent', allowed);

    if (allowed) {
      this.#startTracking();
    } else {
      this.#stopTracking();

      if (wasAllowed) {
        this.#close(false); // revoked: close a popup opened by the visit logic
      }

      removeState(); // no consent (any more): delete stored history
    }
  }

  /* Tracking ----------------------------------------------------------------------------------- */

  #startTracking() {
    if (!storageAvailable()) {
      this.#log('localStorage not available, popup disabled');
      return;
    }

    this.#trackingController = new AbortController();
    const options = { signal: this.#trackingController.signal, passive: true };
    const onActivity = () => this.#onActivity(false);

    ['pointerdown', 'keydown', 'scroll'].forEach((type) => window.addEventListener(type, onActivity, options));
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && this.#onActivity(true), options);
    window.addEventListener('pagehide', () => this.#onActivity(true), options);
    window.addEventListener('storage', this.#onStorage.bind(this), options);

    this.#onActivity(true);
  }

  #stopTracking() {
    this.#trackingController?.abort();
    this.#trackingController = null;
    this.#state = null;
    this.#initialized = false;
  }

  async #onActivity(force) {
    const now = Date.now();

    if (!this.#trackingController || (!force && now - this.#lastActivity < ACTIVITY_THROTTLE)) {
      return;
    }

    this.#lastActivity = now;

    const isFirstCall = !this.#initialized;
    this.#initialized = true;
    const { state, newSession } = await registerActivity(this.#config);
    this.#state = state;

    if (newSession) {
      this.#log(`new session, visit #${state.visits}`, state);
    }

    if (isFirstCall) {
      await this.#applyKnownFacts();
    }

    // Evaluate on every page load and whenever a new session starts in a long-open tab
    if (isFirstCall || newSession) {
      this.#maybeShow();
    }
  }

  /** Facts known from the server (logged-in customer) or the URL are persisted locally. */
  async #applyKnownFacts() {
    const { customerHasOrdered, customerSubscribed } = this.#config;
    const subscribedViaForm = this.#subscribedViaForm;
    const popupSignup = this.#popupSignup;

    if (!customerHasOrdered && !customerSubscribed && !subscribedViaForm && !popupSignup) {
      return;
    }

    this.#state = (await updateState((state) => {
      if (customerHasOrdered) state.hasOrdered = true;
      if (customerSubscribed || subscribedViaForm) state.newsletterSubscribed = true;
      if (popupSignup) state.popupSignup = true;
    })) ?? this.#state;

    this.#log('applied known facts', this.#state);
  }

  #onStorage(event) {
    if (event.key !== STORAGE_KEY && event.key !== null) {
      return;
    }

    this.#state = readState();

    // History deleted in another tab (e.g. consent revoked) or subscription completed there
    if (!this.#state || this.#state.newsletterSubscribed) {
      this.#close(false);
    }
  }

  /* Popup -------------------------------------------------------------------------------------- */

  async #maybeShow() {
    if (!this.dialog || this.dialog.open || this.#evaluating || this.#suppressAutoOpen) {
      return;
    }

    const reason = qualify(this.#state, this.#config);

    if (reason) {
      this.#log('not qualified:', reason);
      return;
    }

    this.#evaluating = true;

    try {
      if (this.#config.delay) {
        await new Promise((resolve) => setTimeout(resolve, this.#config.delay));
      }

      // Claim the popup atomically, so only one tab shows it
      let claimed = false;

      const state = await updateState((current, now) => {
        if (!this.#consent || qualify(current, this.#config, now)) return false;

        current.popupShown = true;
        current.dismissedAt = now; // cooldown starts on display, renewed on close
        claimed = true;
      });

      this.#state = state;

      if (claimed) {
        this.#open();
      }
    } finally {
      this.#evaluating = false;
    }
  }

  #open() {
    try {
      this.dialog.showModal();
    } catch (error) {
      this.#log('could not open dialog', error);
      return;
    }

    document.documentElement.classList.add(LOCK_CLASS);
    this.#log('popup shown');
  }

  #close(recordDismissal = true) {
    if (this.dialog?.open) {
      this.dialog.close(recordDismissal ? 'dismissed' : 'silent');
    }
  }

  #unlockScroll() {
    document.documentElement.classList.remove(LOCK_CLASS);
  }

  #bindDialog() {
    const { signal } = this.#abortController;

    this.dialog.addEventListener('close', () => {
      this.#unlockScroll();

      if (this.dialog.returnValue !== 'silent' && this.#trackingController && !window.Shopify?.designMode) {
        updateState((state, now) => {
          state.dismissedAt = now;
        }).then((state) => {
          this.#state = state;
        });
      }

      this.dialog.returnValue = '';
    }, { signal });

    // Escape fires "cancel", which closes the dialog natively; X button and backdrop click:
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-rv-close]')) {
        this.#close();
      }
    }, { signal });
  }

  /* Theme editor ------------------------------------------------------------------------------- */

  #bindThemeEditor() {
    const section = this.closest('.shopify-section');
    const { signal } = this.#abortController;

    if (!section || !this.dialog) return;

    section.addEventListener('shopify:section:select', () => this.dialog.open || this.#open(), { signal });
    section.addEventListener('shopify:section:deselect', () => this.#close(false), { signal });
    section.addEventListener('shopify:block:select', () => this.dialog.open || this.#open(), { signal });
  }
}

if (!window.customElements.get('returning-visitor-popup')) {
  window.customElements.define('returning-visitor-popup', ReturningVisitorPopup);
}

/* Debug helpers (only exposed when the section's debug setting is on) */
if (document.querySelector('returning-visitor-popup[data-debug]')) {
  window.returningVisitorDebug = {
    state: () => readState(),
    reset: () => removeState(),
    // Moves the last activity back by more than the maximum session timeout setting (120 min)
    expireSession: () => updateState((state) => {
      state.lastVisit -= 121 * MINUTE;
      state.firstVisit = Math.min(state.firstVisit, state.lastVisit);
    })
  };
}
